import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Upload, 
  X, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  Eye,
  Edit,
  Trash2,
  FileImage,
  Video,
  Grid,
  Filter,
  Search,
  RotateCcw,
  Download,
  ExternalLink,
  MessageSquare,
  User,
  Calendar,
  Tag,
  Check,
  XCircle,
  Camera,
  Loader2,
  Minus
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Checkbox } from '../../components/ui/Checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { FloatingOrbs } from '../../components/auth/FloatingOrbs';
import { contentCreationService } from '../../services/contentCreationService';
import { 
  ContentCreationSubmission, 
  ContentCreationFormData, 
  ContentReviewAction,
  PLATFORM_OPTIONS,
  STATUS_COLORS,
  STATUS_LABELS,
  SocialPlatform,
  ContentApprovalStatus
} from '../../lib/types/content-creation';

// Thumbnail Selector Component for Videos
interface ThumbnailSelectorProps {
  videoFile: File | null;
  videoUrl?: string; // For editing existing content
  onThumbnailSelect: (thumbnailFile: File) => void;
  onClose: () => void;
}

interface CropArea {
  x: number;
  y: number;
  size: number;
}

const ThumbnailSelector: React.FC<ThumbnailSelectorProps> = ({ 
  videoFile, 
  videoUrl,
  onThumbnailSelect, 
  onClose 
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cropCanvasRef = useRef<HTMLCanvasElement>(null);
  const cropOverlayRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isCapturing, setIsCapturing] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Crop area state (as percentage of video dimensions)
  const [cropArea, setCropArea] = useState<CropArea>({
    x: 25, // 25% from left
    y: 25, // 25% from top
    size: 50 // 50% of the smaller dimension
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      console.log('Video metadata loaded, duration:', video.duration);
      setDuration(video.duration);
      setCurrentTime(0);
      setVideoLoaded(true);
      video.pause();
    };

    const handleCanPlay = () => {
      console.log('Video can play');
      video.pause(); // Ensure it stays paused
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      
      // Clean up timeout
      if (seekTimeoutRef.current) {
        clearTimeout(seekTimeoutRef.current);
      }
    };
  }, []);

  const handleTimeSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video || !videoLoaded) {
      console.log('Video not ready for seeking');
      return;
    }
    
    const newTime = parseFloat(e.target.value);
    console.log('Seeking to time:', newTime);
    
    setCurrentTime(newTime);
    
    // Debounce the actual video seeking to prevent conflicts
    if (seekTimeoutRef.current) {
      clearTimeout(seekTimeoutRef.current);
    }
    
    seekTimeoutRef.current = setTimeout(() => {
      if (video && videoLoaded) {
        video.currentTime = newTime;
        console.log('Actually seeking to:', newTime);
      }
    }, 100);
  };

  const handleSliderInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Update the visual state immediately while dragging
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    
    // Also trigger the debounced seek
    handleTimeSeek(e);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!cropOverlayRef.current) return;
    
    const rect = cropOverlayRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setIsDragging(true);
    setDragStart({ x, y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !cropOverlayRef.current) return;
    
    const rect = cropOverlayRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    const deltaX = x - dragStart.x;
    const deltaY = y - dragStart.y;
    
    setCropArea(prev => ({
      ...prev,
      x: Math.max(0, Math.min(100 - prev.size, prev.x + deltaX)),
      y: Math.max(0, Math.min(100 - prev.size, prev.y + deltaY))
    }));
    
    setDragStart({ x, y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleSizeChange = (delta: number) => {
    setCropArea(prev => {
      const newSize = Math.max(20, Math.min(80, prev.size + delta));
      return {
        ...prev,
        size: newSize,
        x: Math.max(0, Math.min(100 - newSize, prev.x)),
        y: Math.max(0, Math.min(100 - newSize, prev.y))
      };
    });
  };

  const captureThumbnail = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const cropCanvas = cropCanvasRef.current;
    
    if (!video || !canvas || !cropCanvas) return;

    setIsCapturing(true);
    
    try {
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw current frame to canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');
      
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Calculate crop dimensions
      const videoAspect = video.videoWidth / video.videoHeight;
      const cropSizeInPixels = Math.min(video.videoWidth, video.videoHeight) * (cropArea.size / 100);
      const cropX = (video.videoWidth * cropArea.x) / 100;
      const cropY = (video.videoHeight * cropArea.y) / 100;
      
      // Set crop canvas to square dimensions
      const cropSize = 400; // Final thumbnail size
      cropCanvas.width = cropSize;
      cropCanvas.height = cropSize;
      
      // Draw cropped area to crop canvas
      const cropCtx = cropCanvas.getContext('2d');
      if (!cropCtx) throw new Error('Could not get crop canvas context');
      
      cropCtx.drawImage(
        video,
        cropX,
        cropY,
        cropSizeInPixels,
        cropSizeInPixels,
        0,
        0,
        cropSize,
        cropSize
      );
      
      // Convert crop canvas to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        cropCanvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to create thumbnail'));
        }, 'image/jpeg', 0.85);
      });
      
      // Create file from blob
      const thumbnailFile = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });
      
      onThumbnailSelect(thumbnailFile);
      toast.success('Square thumbnail captured successfully!');
      onClose();
      
    } catch (error) {
      console.error('Error capturing thumbnail:', error);
      toast.error('Failed to capture thumbnail');
    } finally {
      setIsCapturing(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get the video source - either from file or URL
  const videoSource = videoFile ? URL.createObjectURL(videoFile) : videoUrl;

  if (!videoFile && !videoUrl) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="max-w-5xl w-full max-h-[90vh] overflow-y-auto bg-slate-950/95 backdrop-blur-2xl border border-white/[0.08] rounded-3xl shadow-2xl"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
                <Camera size={24} className="text-emerald-400" />
                <span>Select Square Thumbnail</span>
              </h3>
              <p className="text-sm text-white/60 mt-2">
                ⚠️ This cropping is only for the thumbnail preview. Your original video remains unchanged.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Video Player with Crop Selection */}
            <div className="space-y-4">
              <div className="relative aspect-video rounded-xl overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 border border-white/[0.08]">
                {!videoLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center space-x-2 text-white/70">
                      <Loader2 size={20} className="animate-spin" />
                      <span>Loading video...</span>
                    </div>
                  </div>
                )}
                <video
                  ref={videoRef}
                  src={videoSource}
                  className="w-full h-full object-contain"
                  controls={false}
                  preload="metadata"
                  muted
                  playsInline
                  disablePictureInPicture
                />
                
                {/* Crop Selection Overlay */}
                {videoLoaded && (
                  <div
                    ref={cropOverlayRef}
                    className="absolute inset-0 cursor-crosshair"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    {/* Crop Area */}
                    <div
                      className="absolute border-2 border-emerald-400 bg-emerald-400/10 backdrop-blur-sm"
                      style={{
                        left: `${cropArea.x}%`,
                        top: `${cropArea.y}%`,
                        width: `${cropArea.size}%`,
                        height: `${cropArea.size}%`,
                        aspectRatio: '1',
                      }}
                    >
                      {/* Corner handles */}
                      <div className="absolute -top-1 -left-1 w-3 h-3 bg-emerald-400 rounded-full"></div>
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full"></div>
                      <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-emerald-400 rounded-full"></div>
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full"></div>
                      
                      {/* Center text */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                          Crop Area
                        </span>
                      </div>
                    </div>
                    
                    {/* Darkened overlay outside crop area */}
                    <div className="absolute inset-0 bg-black/40 pointer-events-none" 
                         style={{
                           mask: `radial-gradient(circle at ${cropArea.x + cropArea.size/2}% ${cropArea.y + cropArea.size/2}%, transparent ${cropArea.size/2}%, black ${cropArea.size/2 + 0.1}%)`
                         }}
                    />
                  </div>
                )}
              </div>

              {/* Timeline Scrubber */}
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm text-white/70">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
                
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  value={currentTime}
                  onChange={handleTimeSeek}
                  onInput={handleSliderInput}
                  disabled={!videoLoaded}
                  className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer slider-thumb"
                />
              </div>
            </div>

            {/* Crop Controls & Preview */}
            <div className="space-y-4">
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">Crop Controls</h4>
                
                {/* Size Control */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-white/80">
                    Crop Size: {Math.round(cropArea.size)}%
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => handleSizeChange(-5)}
                      className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                    >
                      <Minus size={16} />
                    </button>
                    <input
                      type="range"
                      min="20"
                      max="80"
                      value={cropArea.size}
                      onChange={(e) => setCropArea(prev => ({ ...prev, size: parseInt(e.target.value) }))}
                      className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
                    />
                    <button
                      onClick={() => handleSizeChange(5)}
                      className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {/* Position Info */}
                <div className="text-sm text-white/60 mt-3">
                  <p>Position: {Math.round(cropArea.x)}%, {Math.round(cropArea.y)}%</p>
                  <p className="text-xs mt-1">Drag the crop area to reposition</p>
                </div>
              </div>

              {/* Preview */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">Thumbnail Preview</h4>
                <div className="aspect-square bg-white/[0.06] rounded-2xl border border-white/[0.08] overflow-hidden">
                  <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                    <span className="text-white/50 text-sm">Live preview will appear here</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4">
                <Button
                  onClick={onClose}
                  variant="ghost"
                  className="px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-white/30 text-white/70 hover:text-white rounded-xl font-medium transition-all duration-300"
                >
                  Cancel
                </Button>
                
                <Button
                  onClick={captureThumbnail}
                  disabled={isCapturing || !videoLoaded}
                  className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl border border-emerald-400/30"
                >
                  {isCapturing ? (
                    <>
                      <Loader2 size={16} className="animate-spin mr-2" />
                      Capturing...
                    </>
                  ) : (
                    <>
                      <Camera size={16} className="mr-2" />
                      Capture Square Thumbnail
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Hidden canvases for image processing */}
      <canvas ref={canvasRef} className="hidden" />
      <canvas ref={cropCanvasRef} className="hidden" />
    </motion.div>
  );
};

const ContentCreationPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [submissions, setSubmissions] = useState<ContentCreationSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<ContentCreationSubmission | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ContentApprovalStatus | 'all'>('all');
  const [viewMode, setViewMode] = useState<'user' | 'admin'>('user');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showThumbnailSelector, setShowThumbnailSelector] = useState(false);
  const [editingThumbnailId, setEditingThumbnailId] = useState<string | null>(null);
  const [updatingThumbnail, setUpdatingThumbnail] = useState(false);
  const [expandedFeedback, setExpandedFeedback] = useState<Set<string>>(new Set());

  // Form state
  const [formData, setFormData] = useState<ContentCreationFormData>({
    title: '',
    caption: '',
    platform: [],
    mediaFiles: [],
    thumbnailFile: undefined,
    selectedThumbnailIndex: undefined,
    promotedItems: '',
  });

  // Review form state
  const [reviewAction, setReviewAction] = useState<ContentReviewAction>({
    action: 'approve',
    feedback: '',
    approvalNotes: '',
    rejectionReason: '',
  });

  // Check if user has admin access
  const isAdmin = currentUser?.roles?.some(role => 
    ['Master Admin', 'Marketing', 'Marketing Admin'].includes(role)
  ) || false;

  // Check if user can delete content
  const canDeleteContent = currentUser?.roles?.some(role => 
    ['Master Admin', 'Marketing Admin'].includes(role)
  ) || false;

  // Check if user is master admin
  const isMasterAdmin = () => {
    return currentUser?.roles?.some(role => role === 'Master Admin') || false;
  };

  // Load submissions on component mount
  useEffect(() => {
    if (!currentUser) return;

    setLoading(true);
    
    // Set up real-time listeners based on user role
    const unsubscribe = isAdmin && viewMode === 'admin'
      ? contentCreationService.listenToAllSubmissions((data) => {
          setSubmissions(data);
          setLoading(false);
        })
      : contentCreationService.listenToUserSubmissions(currentUser.uid, (data) => {
          setSubmissions(data);
          setLoading(false);
        });

    return () => unsubscribe();
  }, [currentUser, isAdmin, viewMode]);

  // Handle file drop
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const validFiles = acceptedFiles.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type.startsWith('video/');
      const isValidSize = file.size <= 500 * 1024 * 1024; // 500MB limit
      
      if (!isValidType) {
        toast.error(`${file.name} is not a valid image or video file`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name} is too large. Max size is 500MB`);
        return false;
      }
      return true;
    });

    setFormData(prev => ({
      ...prev,
      mediaFiles: [...prev.mediaFiles, ...validFiles]
    }));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.mov', '.avi', '.webm']
    },
    multiple: true,
    maxSize: 500 * 1024 * 1024, // 500MB
  });

  // Remove file from upload list
  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      mediaFiles: prev.mediaFiles.filter((_, i) => i !== index)
    }));
  };

  // Handle platform selection
  const togglePlatform = (platform: SocialPlatform) => {
    setFormData(prev => ({
      ...prev,
      platform: prev.platform.includes(platform)
        ? prev.platform.filter(p => p !== platform)
        : [...prev.platform, platform]
    }));
  };

  // Handle thumbnail file upload (for videos)
  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file for the thumbnail');
      return;
    }

    // Validate file size (10MB limit for thumbnails)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Thumbnail file must be smaller than 10MB');
      return;
    }

    setFormData(prev => ({
      ...prev,
      thumbnailFile: file
    }));
  };

  // Remove thumbnail file
  const removeThumbnail = () => {
    setFormData(prev => ({
      ...prev,
      thumbnailFile: undefined
    }));
  };

  // Select thumbnail from uploaded images (for carousels)
  const selectCarouselThumbnail = (index: number) => {
    setFormData(prev => ({
      ...prev,
      selectedThumbnailIndex: index
    }));
  };

  // Open thumbnail selector for videos
  const openThumbnailSelector = () => {
    const videoFile = formData.mediaFiles.find(file => file.type.startsWith('video/'));
    if (!videoFile) {
      toast.error('Please upload a video file first');
      return;
    }
    setShowThumbnailSelector(true);
  };

  // Handle thumbnail selection from video
  const handleThumbnailSelect = (thumbnailFile: File) => {
    if (editingThumbnailId) {
      // Handle thumbnail update for existing content
      handleThumbnailUpdate(editingThumbnailId, thumbnailFile);
    } else {
      // Handle thumbnail selection for new content
      setFormData(prev => ({
        ...prev,
        thumbnailFile
      }));
      setShowThumbnailSelector(false);
    }
  };

  const openThumbnailEditor = (submissionId: string) => {
    setEditingThumbnailId(submissionId);
    setShowThumbnailSelector(true);
  };

  const handleThumbnailUpdate = async (submissionId: string, thumbnailFile: File) => {
    if (!currentUser) return;
    
    setUpdatingThumbnail(true);
    
    try {
      await contentCreationService.updateContentThumbnail(submissionId, thumbnailFile, currentUser.uid);
      toast.success('Thumbnail updated successfully!');
      setShowThumbnailSelector(false);
      setEditingThumbnailId(null);
    } catch (error: any) {
      console.error('Error updating thumbnail:', error);
      toast.error(error.message || 'Failed to update thumbnail');
    } finally {
      setUpdatingThumbnail(false);
    }
  };

  // Get media type based on uploaded files
  const getMediaType = (files: File[]): 'image' | 'video' | 'carousel' => {
    if (files.length > 1) return 'carousel';
    if (files.length === 1) {
      return files[0].type.startsWith('video/') ? 'video' : 'image';
    }
    return 'image';
  };

  // Check if thumbnail selection is needed
  const needsThumbnailSelection = () => {
    const mediaType = getMediaType(formData.mediaFiles);
    return mediaType === 'video' || mediaType === 'carousel';
  };

  // Submit content
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      toast.error('You must be logged in to submit content');
      return;
    }

    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a title for your content');
      return;
    }
    if (!formData.caption.trim()) {
      toast.error('Please enter a caption for your content');
      return;
    }
    if (formData.platform.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }
    if (formData.mediaFiles.length === 0) {
      toast.error('Please upload at least one image or video');
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log('🚀 Starting content submission...', {
        userId: currentUser.uid,
        userName: currentUser.displayName,
        userEmail: currentUser.email,
        formData: {
          title: formData.title,
          caption: formData.caption.substring(0, 50) + '...',
          platforms: formData.platform,
          fileCount: formData.mediaFiles.length,
          promotedItems: formData.promotedItems
        }
      });

      const submissionId = await contentCreationService.submitContent(
        formData,
        currentUser.uid,
        currentUser.displayName || 'Unknown User',
        currentUser.email || ''
      );
      
      console.log('✅ Content submitted successfully with ID:', submissionId);
      toast.success('Content submitted successfully!');
      setShowSubmissionForm(false);
      setFormData({
        title: '',
        caption: '',
        platform: [],
        mediaFiles: [],
        thumbnailFile: undefined,
        selectedThumbnailIndex: undefined,
        promotedItems: '',
      });
    } catch (error: any) {
      console.error('❌ Error submitting content:', error);
      
      // Show detailed error information in production
      let errorMessage = 'Failed to submit content. Please try again.';
      let detailMessage = '';
      
      if (error?.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your account permissions.';
        detailMessage = 'Your account may not have permission to submit content.';
      } else if (error?.code === 'storage/unauthorized') {
        errorMessage = 'File upload failed. Please check storage permissions.';
        detailMessage = 'The file could not be uploaded to storage.';
      } else if (error?.code === 'storage/quota-exceeded') {
        errorMessage = 'Storage quota exceeded. Please contact your administrator.';
        detailMessage = 'The storage limit has been reached.';
      } else if (error?.code === 'storage/retry-limit-exceeded') {
        errorMessage = 'Upload failed after multiple attempts. Please try again later.';
        detailMessage = 'Network issues may be causing upload failures.';
      } else if (error?.message?.includes('index')) {
        errorMessage = 'Database indexing in progress. Please try again in a few minutes.';
        detailMessage = 'The database is still setting up indexes for content submissions.';
      } else if (error?.message?.includes('network')) {
        errorMessage = 'Network error. Please check your internet connection.';
        detailMessage = 'Unable to connect to the server.';
      } else if (error?.code === 'unauthenticated') {
        errorMessage = 'Authentication error. Please try logging out and back in.';
        detailMessage = 'Your login session may have expired.';
      } else if (error?.message) {
        errorMessage = `Submission failed: ${error.message}`;
        detailMessage = `Error code: ${error?.code || 'unknown'}`;
      } else {
        errorMessage = 'An unexpected error occurred. Please try again.';
        detailMessage = 'No error details available.';
      }
      
      // Show main error
      toast.error(errorMessage);
      
      // Show detail message if available
      if (detailMessage) {
        setTimeout(() => {
          toast.error(detailMessage, { duration: 8000 });
        }, 1000);
      }
      
      // Show technical details for debugging
      setTimeout(() => {
        toast.error(`Debug Info: ${JSON.stringify({
          code: error?.code || 'no-code',
          message: error?.message || 'no-message',
          name: error?.name || 'no-name',
          constructor: error?.constructor?.name || 'no-constructor'
        })}`, { duration: 15000 });
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle content review (admin only)
  const handleReview = async () => {
    if (!selectedSubmission || !currentUser) return;

    try {
      await contentCreationService.reviewContent(
        selectedSubmission.id,
        reviewAction,
        currentUser.uid,
        currentUser.displayName || 'Unknown Reviewer'
      );
      
      const actionText = reviewAction.action === 'approve' ? 'approved' : 
                        reviewAction.action === 'deny' ? 'denied' : 'marked for rework';
      toast.success(`Content ${actionText} successfully!`);
      
      setShowReviewModal(false);
      setSelectedSubmission(null);
      setReviewAction({
        action: 'approve',
        feedback: '',
        approvalNotes: '',
        rejectionReason: '',
      });
    } catch (error) {
      console.error('Error reviewing content:', error);
      toast.error('Failed to review content. Please try again.');
    }
  };

  // Handle content deletion (admin only)
  const handleDelete = async (submissionId: string) => {
    if (!currentUser || !canDeleteContent) {
      toast.error('You do not have permission to delete content');
      return;
    }

    if (!confirm('Are you sure you want to delete this content? This action cannot be undone.')) {
      return;
    }

    setDeletingId(submissionId);

    try {
      await contentCreationService.deleteContent(submissionId);
      toast.success('Content deleted successfully');
      
      // Update local state to remove the deleted item
      setSubmissions(prev => prev.filter(submission => submission.id !== submissionId));
      
    } catch (error: any) {
      console.error('Error deleting content:', error);
      toast.error(error.message || 'Failed to delete content');
    } finally {
      setDeletingId(null);
    }
  };

  // Filter submissions
  const filteredSubmissions = submissions.filter(submission => {
    const matchesSearch = searchTerm === '' || 
      submission.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.caption.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.submittedByName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Get status icon
  const getStatusIcon = (status: ContentApprovalStatus) => {
    switch (status) {
      case 'submitted':
      case 'resubmitted':
        return <Clock className="h-4 w-4" />;
      case 'under_review':
        return <Eye className="h-4 w-4" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'denied':
        return <XCircle className="h-4 w-4" />;
      case 'rework_requested':
        return <RotateCcw className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  // Get media icon
  const getMediaIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'carousel': return <Grid className="h-4 w-4" />;
      default: return <FileImage className="h-4 w-4" />;
    }
  };

  // Toggle feedback notes visibility
  const toggleFeedbackNotes = (submissionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedFeedback(prev => {
      const newSet = new Set(prev);
      if (newSet.has(submissionId)) {
        newSet.delete(submissionId);
      } else {
        newSet.add(submissionId);
      }
      return newSet;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
      {/* Floating Orbs Background */}
      <FloatingOrbs />
      
      {/* Main Content */}
      <div className="relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="pl-12 pr-8 pt-8 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-4xl font-light bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-600 bg-clip-text text-transparent">
                Content Creation
              </h1>
              <p className="mt-1 text-white">
                Submit and manage your social media content for approval
              </p>
              <div className="h-px bg-gradient-to-r from-emerald-400/50 to-transparent mt-6 max-w-[40rem]" />
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
              {/* Quick Guide - Compact */}
              <div className="flex items-center gap-2 bg-white/[0.03] backdrop-blur-2xl border border-white/[0.06] rounded-lg px-3 py-2 shadow-lg">
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = '/content-creation-guide.pdf';
                    link.download = 'Content-Creation-Guide.pdf';
                    link.click();
                  }}
                  className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-105"
                  title="Download Quick Guide"
                >
                  <Download className="h-4 w-4 text-white" />
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-medium">Quick Guide</span>
                  <Button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = '/content-creation-guide.pdf';
                      link.download = 'Content-Creation-Guide.pdf';
                      link.click();
                    }}
                    variant="ghost"
                    className="text-white/70 hover:text-white hover:bg-white/10 px-2 py-1 h-auto text-xs rounded-md"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
              
              {/* View Mode Toggle (Admin Only) */}
              {isAdmin && (
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => setViewMode('user')}
                    className={`
                      flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-300 
                      backdrop-blur-xl border shadow-lg hover:shadow-xl whitespace-nowrap
                      ${viewMode === 'user' 
                        ? 'bg-white/[0.15] border-white/[0.25] text-white hover:bg-white/[0.2] hover:border-white/[0.3]' 
                        : 'bg-white/[0.08] border-white/[0.12] text-white/80 hover:bg-white/[0.12] hover:border-white/[0.2] hover:text-white'
                      }
                    `}
                  >
                    <User className="h-4 w-4 flex-shrink-0" />
                    <span>My Content</span>
                  </Button>
                  <Button
                    onClick={() => setViewMode('admin')}
                    className={`
                      flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-300 
                      backdrop-blur-xl border shadow-lg hover:shadow-xl whitespace-nowrap
                      ${viewMode === 'admin' 
                        ? 'bg-white/[0.15] border-white/[0.25] text-white hover:bg-white/[0.2] hover:border-white/[0.3]' 
                        : 'bg-white/[0.08] border-white/[0.12] text-white/80 hover:bg-white/[0.12] hover:border-white/[0.2] hover:text-white'
                      }
                    `}
                  >
                    <Eye className="h-4 w-4 flex-shrink-0" />
                    <span>All Content</span>
                  </Button>
                </div>
              )}
              
              {/* Submit Content Button */}
              <Button
                onClick={() => setShowSubmissionForm(true)}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-all duration-300 
                           bg-gradient-to-r from-emerald-500/90 to-emerald-600/90 hover:from-emerald-600 hover:to-emerald-700 
                           backdrop-blur-xl border border-emerald-400/30 text-white shadow-lg hover:shadow-xl 
                           hover:shadow-emerald-500/25 whitespace-nowrap justify-center sm:justify-start"
              >
                <Plus className="h-4 w-4 flex-shrink-0" />
                <span>Submit Content</span>
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Filters - Compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="pl-12 pr-8 mb-6"
        >
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  type="text"
                  placeholder="Search content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 py-1.5 h-9 bg-white/[0.08] border-white/[0.12] text-white placeholder-white/40 rounded-lg focus:bg-white/[0.12] focus:border-white/20 transition-all duration-300"
                />
              </div>
            </div>
            
            {/* Status Filter */}
            <div className="w-full sm:w-48">
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as any)}>
                <SelectTrigger className="bg-white/[0.08] border-white/[0.12] text-white py-1.5 h-9 rounded-lg focus:bg-white/[0.12] focus:border-white/20 transition-all duration-300">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900/95 backdrop-blur-xl border-slate-700/50 shadow-2xl">
                  <SelectItem value="all" className="text-white hover:bg-white/10 py-2">All Status</SelectItem>
                  <SelectItem value="submitted" className="text-white hover:bg-white/10 py-2">Submitted</SelectItem>
                  <SelectItem value="under_review" className="text-white hover:bg-white/10 py-2">Under Review</SelectItem>
                  <SelectItem value="approved" className="text-white hover:bg-white/10 py-2">Approved</SelectItem>
                  <SelectItem value="denied" className="text-white hover:bg-white/10 py-2">Denied</SelectItem>
                  <SelectItem value="rework_requested" className="text-white hover:bg-white/10 py-2">Rework Requested</SelectItem>
                  <SelectItem value="resubmitted" className="text-white hover:bg-white/10 py-2">Resubmitted</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </motion.div>

        {/* Content Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="pl-12 pr-8"
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              {/* Empty state content */}
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
              {filteredSubmissions.map((submission, index) => (
                <motion.div
                  key={submission.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  className="group cursor-pointer"
                  onClick={() => {
                    setSelectedSubmission(submission);
                    if (isAdmin && viewMode === 'admin') {
                      setShowReviewModal(true);
                    } else {
                      setShowDetailModal(true);
                    }
                  }}
                >
                  {/* Media Preview */}
                  {submission.mediaFiles.length > 0 && (
                    <div className="relative overflow-hidden bg-transparent aspect-square flex items-center justify-center mb-3">
                      {submission.thumbnailUrl ? (
                        <img
                          src={submission.thumbnailUrl}
                          alt={submission.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : submission.mediaType === 'video' ? (
                        <video
                          src={submission.mediaFiles[0]}
                          className="w-full h-full object-contain bg-gray-900"
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <img
                          src={submission.mediaFiles[0]}
                          alt={submission.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      )}
                      
                      {/* Status Badge */}
                      <div className="absolute top-2 right-2">
                        <div 
                          className="px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium text-white"
                          style={{ backgroundColor: STATUS_COLORS[submission.status] }}
                        >
                          {getStatusIcon(submission.status)}
                          <span>{STATUS_LABELS[submission.status]}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Content Info */}
                  <div className="space-y-2">
                    <div>
                      <h3 className="font-semibold text-white text-lg mb-1 line-clamp-1">
                        {submission.title}
                      </h3>
                      <p className="text-white/70 text-sm line-clamp-3">
                        {submission.caption}
                      </p>
                    </div>

                    {/* Platform Tags */}
                    <div className="flex flex-wrap gap-1">
                      {submission.platform.map(platform => {
                        const platformOption = PLATFORM_OPTIONS.find(p => p.value === platform);
                        return (
                          <span
                            key={platform}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs"
                          >
                            <span>{platformOption?.label}</span>
                          </span>
                        );
                      })}
                    </div>

                    {/* Metadata */}
                    <div className="space-y-0.5 text-xs text-white/50">
                      <div className="flex items-center gap-2">
                        <User className="h-3 w-3" />
                        <span>{submission.submittedByName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDistanceToNow(submission.submittedAt, { addSuffix: true })}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Submission Form Modal */}
      <AnimatePresence>
        {showSubmissionForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowSubmissionForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-950/95 backdrop-blur-2xl border border-white/[0.08] rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Submit Content for Approval</h2>
                  <button
                    onClick={() => setShowSubmissionForm(false)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Content Title *
                    </label>
                    <Input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter a descriptive title for your content"
                      className="bg-white/[0.08] border-white/[0.12] text-white placeholder-white/40 rounded-xl py-3 focus:bg-white/[0.12] focus:border-white/20 transition-all duration-300"
                      required
                    />
                  </div>

                  {/* Caption */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Caption *
                    </label>
                    <Textarea
                      value={formData.caption}
                      onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
                      placeholder="Write your social media caption here..."
                      rows={4}
                      className="bg-white/[0.08] border-white/[0.12] text-white placeholder-white/40 rounded-xl p-4 focus:bg-white/[0.12] focus:border-white/20 transition-all duration-300"
                      required
                    />
                  </div>

                  {/* Platform Selection */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-3">
                      Target Platforms *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {PLATFORM_OPTIONS.map(option => (
                        <label
                          key={option.value}
                          className="flex items-center space-x-3 p-4 rounded-xl border border-white/[0.12] hover:border-white/25 hover:bg-white/[0.05] cursor-pointer transition-all duration-300"
                        >
                          <Checkbox
                            checked={formData.platform.includes(option.value)}
                            onCheckedChange={() => togglePlatform(option.value)}
                            className="border-white/40"
                          />
                          <span className="text-white">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-3">
                      Media Files *
                    </label>
                    
                    {/* Dropzone */}
                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed border-white/20 rounded-2xl p-10 text-center transition-all duration-300 cursor-pointer ${
                        isDragActive ? 'border-emerald-400 bg-emerald-400/10 scale-105' : 'hover:border-white/40 hover:bg-white/[0.02]'
                      }`}
                    >
                      <input {...getInputProps()} />
                      <Upload className="h-12 w-12 text-white/50 mx-auto mb-4" />
                      <p className="text-white mb-2">
                        {isDragActive
                          ? 'Drop files here...'
                          : 'Drag & drop images or videos here, or click to select'
                        }
                      </p>
                      <p className="text-white/50 text-sm">
                        Supports: JPG, PNG, GIF, WebP, MP4, MOV, AVI, WebM (Max: 500MB each)
                      </p>
                    </div>

                    {/* File Preview */}
                    {formData.mediaFiles.length > 0 && (
                      <div className="mt-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {formData.mediaFiles.map((file, index) => {
                            const isSelected = getMediaType(formData.mediaFiles) === 'carousel' && 
                                             formData.selectedThumbnailIndex === index;
                            return (
                              <div key={index} className="relative group">
                                <div className={`bg-white/[0.06] rounded-2xl overflow-hidden aspect-square border transition-all duration-300 ${
                                  isSelected ? 'border-emerald-400 bg-emerald-400/10' : 'border-white/[0.08]'
                                }`}>
                                  {file.type.startsWith('video/') ? (
                                    <video
                                      src={URL.createObjectURL(file)}
                                      className="w-full h-full object-cover"
                                      muted
                                      preload="metadata"
                                    />
                                  ) : (
                                    <img
                                      src={URL.createObjectURL(file)}
                                      alt={`Preview ${index + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                  
                                  {/* Thumbnail selection button for carousels */}
                                  {getMediaType(formData.mediaFiles) === 'carousel' && file.type.startsWith('image/') && (
                                    <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                                      <Button
                                        type="button"
                                        onClick={() => selectCarouselThumbnail(index)}
                                        className={`${
                                          isSelected
                                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                            : 'bg-white/90 hover:bg-white text-slate-900'
                                        } transition-all duration-300`}
                                      >
                                        {isSelected ? 'Selected as Thumbnail' : 'Set as Thumbnail'}
                                      </Button>
                                    </div>
                                  )}
                                  
                                  {/* Selected thumbnail indicator */}
                                  {isSelected && (
                                    <div className="absolute top-2 left-2 bg-emerald-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                                      Thumbnail
                                    </div>
                                  )}
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeFile(index)}
                                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Thumbnail Selection Section */}
                        {needsThumbnailSelection() && (
                          <div className="mt-6 p-4 bg-white/[0.04] rounded-2xl border border-white/[0.08]">
                            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                              <FileImage className="h-4 w-4" />
                              Thumbnail Selection
                            </h4>
                            
                            {getMediaType(formData.mediaFiles) === 'video' && (
                              <div className="space-y-3">
                                <p className="text-white/70 text-sm">
                                  Upload a custom thumbnail image for your video (optional)
                                </p>
                                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                                  <p className="text-blue-300 text-sm">
                                    💡 Your video will be uploaded in its original quality and dimensions. Only thumbnail previews can be cropped.
                                  </p>
                                </div>
                                
                                {!formData.thumbnailFile ? (
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={handleThumbnailUpload}
                                      className="hidden"
                                      id="thumbnail-upload"
                                    />
                                    <label
                                      htmlFor="thumbnail-upload"
                                      className="inline-flex items-center gap-2 px-4 py-2 bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.12] rounded-xl text-white cursor-pointer transition-all duration-300"
                                    >
                                      <Upload className="h-4 w-4" />
                                      Upload Thumbnail
                                    </label>
                                    <span className="text-white/50 text-sm">or</span>
                                    <button
                                      type="button"
                                      onClick={openThumbnailSelector}
                                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 rounded-xl text-emerald-300 hover:text-emerald-200 transition-all duration-300"
                                    >
                                      <Camera className="h-4 w-4" />
                                      Select from Video
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                      <img
                                        src={URL.createObjectURL(formData.thumbnailFile)}
                                        alt="Thumbnail preview"
                                        className="w-16 h-16 object-cover rounded-lg border border-white/[0.12]"
                                      />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-white text-sm font-medium truncate">
                                        {formData.thumbnailFile.name}
                                      </p>
                                      <p className="text-white/60 text-xs">
                                        {(formData.thumbnailFile.size / (1024 * 1024)).toFixed(2)} MB
                                      </p>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={removeThumbnail}
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {getMediaType(formData.mediaFiles) === 'carousel' && (
                              <div className="space-y-3">
                                <p className="text-white/70 text-sm">
                                  {formData.selectedThumbnailIndex !== undefined
                                    ? `Image ${formData.selectedThumbnailIndex + 1} selected as thumbnail`
                                    : 'Click on an image above to set it as the thumbnail for this carousel'
                                  }
                                </p>
                                {formData.selectedThumbnailIndex === undefined && (
                                  <p className="text-yellow-400/80 text-xs">
                                    ⚠️ No thumbnail selected - the first image will be used by default
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Promoted Items */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Specific Items Being Promoted (Optional)
                    </label>
                    <Input
                      type="text"
                      value={formData.promotedItems}
                      onChange={(e) => setFormData(prev => ({ ...prev, promotedItems: e.target.value }))}
                      placeholder="SKU, Item Title, Description, etc."
                      className="bg-white/[0.08] border-white/[0.12] text-white placeholder-white/40 rounded-xl py-3 focus:bg-white/[0.12] focus:border-white/20 transition-all duration-300"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end gap-3 pt-6 border-t border-white/10">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setShowSubmissionForm(false)}
                      className="px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-white/30 text-white/70 hover:text-white rounded-xl font-medium transition-all duration-300 backdrop-blur-sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-8 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl border border-emerald-400/30 backdrop-blur-sm"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Submit
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review Modal (Admin Only) */}
      <AnimatePresence>
        {showReviewModal && selectedSubmission && isAdmin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowReviewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-950/95 backdrop-blur-2xl border border-white/[0.08] rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Review Content</h2>
                  <button
                    onClick={() => setShowReviewModal(false)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Content Preview */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Content Preview</h3>
                    
                    {/* Media */}
                    {selectedSubmission.mediaFiles.length > 0 && (
                      <div className="bg-white/[0.06] rounded-2xl overflow-hidden mb-4 aspect-square border border-white/[0.08]">
                        {selectedSubmission.mediaType === 'video' ? (
                          <video
                            src={selectedSubmission.mediaFiles[0]}
                            controls
                            className="w-full h-full object-contain bg-gray-900"
                            poster={selectedSubmission.thumbnailUrl}
                          />
                        ) : selectedSubmission.thumbnailUrl ? (
                          <img
                            src={selectedSubmission.thumbnailUrl}
                            alt={selectedSubmission.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img
                            src={selectedSubmission.mediaFiles[0]}
                            alt={selectedSubmission.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    )}

                    {/* Content Details */}
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-white mb-1">{selectedSubmission.title}</h4>
                        <p className="text-white/70">{selectedSubmission.caption}</p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {selectedSubmission.platform.map(platform => {
                          const platformOption = PLATFORM_OPTIONS.find(p => p.value === platform);
                          return (
                            <span
                              key={platform}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-sm"
                            >
                              <span>{platformOption?.label}</span>
                            </span>
                          );
                        })}
                      </div>

                      <div className="text-sm text-white/60 space-y-1">
                        <div>Submitted by: {selectedSubmission.submittedByName}</div>
                        <div>Submitted: {formatDistanceToNow(selectedSubmission.submittedAt, { addSuffix: true })}</div>
                        {selectedSubmission.promotedItems && selectedSubmission.promotedItems.length > 0 && (
                          <div>Promoting: {selectedSubmission.promotedItems.join(', ')}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Review Form */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Review Decision</h3>
                    
                    <div className="space-y-4">
                      {/* Action Selection */}
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Action</label>
                        <Select value={reviewAction.action} onValueChange={(value: any) => setReviewAction(prev => ({ ...prev, action: value }))}>
                          <SelectTrigger className="bg-white/10 border-white/20 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600">
                            <SelectItem value="approve" className="text-white hover:bg-white/10">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-green-400" />
                                Approve
                              </div>
                            </SelectItem>
                            <SelectItem value="request_rework" className="text-white hover:bg-white/10">
                              <div className="flex items-center gap-2">
                                <RotateCcw className="h-4 w-4 text-yellow-400" />
                                Rework
                              </div>
                            </SelectItem>
                            <SelectItem value="deny" className="text-white hover:bg-white/10">
                              <div className="flex items-center gap-2">
                                <XCircle className="h-4 w-4 text-red-400" />
                                Deny
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Feedback/Notes */}
                      {reviewAction.action === 'approve' && (
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Approval Notes (Optional)
                          </label>
                          <Textarea
                            value={reviewAction.approvalNotes}
                            onChange={(e) => setReviewAction(prev => ({ ...prev, approvalNotes: e.target.value }))}
                            placeholder="Add any notes about the approval..."
                            rows={3}
                            className="bg-white/10 border-white/20 text-white placeholder-white/50"
                          />
                        </div>
                      )}

                      {reviewAction.action === 'request_rework' && (
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Feedback for Rework *
                          </label>
                          <Textarea
                            value={reviewAction.feedback}
                            onChange={(e) => setReviewAction(prev => ({ ...prev, feedback: e.target.value }))}
                            placeholder="Explain what needs to be changed or improved..."
                            rows={4}
                            className="bg-white/10 border-white/20 text-white placeholder-white/50"
                            required
                          />
                        </div>
                      )}

                      {reviewAction.action === 'deny' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-white mb-2">
                              Reason for Denial *
                            </label>
                            <Textarea
                              value={reviewAction.rejectionReason}
                              onChange={(e) => setReviewAction(prev => ({ ...prev, rejectionReason: e.target.value }))}
                              placeholder="Explain why this content is being denied..."
                              rows={3}
                              className="bg-white/10 border-white/20 text-white placeholder-white/50"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-white mb-2">
                              Additional Feedback (Optional)
                            </label>
                            <Textarea
                              value={reviewAction.feedback}
                              onChange={(e) => setReviewAction(prev => ({ ...prev, feedback: e.target.value }))}
                              placeholder="Any additional feedback for the submitter..."
                              rows={2}
                              className="bg-white/10 border-white/20 text-white placeholder-white/50"
                            />
                          </div>
                        </>
                      )}

                      {/* Submit Review */}
                      <div className="flex justify-center items-center pt-6 border-t border-white/10">
                        <div className="flex items-center gap-3 w-full max-w-md">
                          {/* Delete Button */}
                          {canDeleteContent && (
                            <Button
                              onClick={() => handleDelete(selectedSubmission.id)}
                              disabled={deletingId === selectedSubmission.id}
                              className="flex-1 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-500/50 text-red-400 hover:text-red-300 rounded-xl font-medium transition-all duration-300 backdrop-blur-sm min-w-0"
                            >
                              {deletingId === selectedSubmission.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400 mr-2"></div>
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </>
                              )}
                            </Button>
                          )}
                          
                          <Button
                            variant="ghost"
                            onClick={() => setShowReviewModal(false)}
                            className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-white/30 text-white/70 hover:text-white rounded-xl font-medium transition-all duration-300 backdrop-blur-sm min-w-0"
                          >
                            Cancel
                          </Button>
                          
                          <Button
                            onClick={handleReview}
                            className={`flex-1 py-2.5 rounded-xl font-medium transition-all duration-300 backdrop-blur-sm shadow-lg hover:shadow-xl min-w-0 ${
                              reviewAction.action === 'approve' 
                                ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border border-green-400/30' :
                              reviewAction.action === 'request_rework' 
                                ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white border border-yellow-400/30' :
                                'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white border border-red-400/30'
                            }`}
                          >
                            <div className="flex items-center justify-center">
                              {reviewAction.action === 'approve' && <CheckCircle className="h-4 w-4 mr-2" />}
                              {reviewAction.action === 'request_rework' && <RotateCcw className="h-4 w-4 mr-2" />}
                              {reviewAction.action === 'deny' && <XCircle className="h-4 w-4 mr-2" />}
                              <span className="truncate">
                                {reviewAction.action === 'approve' ? 'Approve' :
                                 reviewAction.action === 'request_rework' ? 'Rework' :
                                 'Deny'}
                              </span>
                            </div>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal for Users */}
      <AnimatePresence>
        {showDetailModal && selectedSubmission && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-950/95 backdrop-blur-2xl border border-white/[0.08] rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Content Details</h2>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Content Preview */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Content Preview</h3>
                    
                    {/* Media */}
                    {selectedSubmission.mediaFiles.length > 0 && (
                      <div className="bg-white/[0.06] rounded-2xl overflow-hidden mb-4 aspect-square border border-white/[0.08]">
                        {selectedSubmission.mediaType === 'video' ? (
                          <video
                            src={selectedSubmission.mediaFiles[0]}
                            controls
                            className="w-full h-full object-contain bg-gray-900"
                            poster={selectedSubmission.thumbnailUrl}
                          />
                        ) : selectedSubmission.thumbnailUrl ? (
                          <img
                            src={selectedSubmission.thumbnailUrl}
                            alt={selectedSubmission.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img
                            src={selectedSubmission.mediaFiles[0]}
                            alt={selectedSubmission.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    )}

                    {/* Content Details */}
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-white mb-1">{selectedSubmission.title}</h4>
                        <p className="text-white/70 leading-relaxed">{selectedSubmission.caption}</p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {selectedSubmission.platform.map(platform => {
                          const platformOption = PLATFORM_OPTIONS.find(p => p.value === platform);
                          return (
                            <span
                              key={platform}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-sm"
                            >
                              <span>{platformOption?.label}</span>
                            </span>
                          );
                        })}
                      </div>

                      <div className="text-sm text-white/60 space-y-1">
                        <div>Submitted: {formatDistanceToNow(selectedSubmission.submittedAt, { addSuffix: true })}</div>
                        {selectedSubmission.promotedItems && selectedSubmission.promotedItems.length > 0 && (
                          <div>Promoting: {selectedSubmission.promotedItems.join(', ')}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Status and Review Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Status & Review</h3>
                    
                    <div className="space-y-4">
                      {/* Current Status */}
                      <div className="bg-white/[0.06] rounded-2xl p-4 border border-white/[0.08]">
                        <div className="flex items-center gap-2 mb-2">
                          <div 
                            className="px-3 py-1 rounded-full flex items-center gap-2 text-sm font-medium text-white"
                            style={{ backgroundColor: STATUS_COLORS[selectedSubmission.status] }}
                          >
                            {getStatusIcon(selectedSubmission.status)}
                            <span>{STATUS_LABELS[selectedSubmission.status]}</span>
                          </div>
                        </div>
                        <div className="text-sm text-white/60">
                          Last updated: {formatDistanceToNow(selectedSubmission.updatedAt, { addSuffix: true })}
                        </div>
                      </div>

                      {/* Review Information */}
                      {selectedSubmission.reviewedBy && (
                        <div className="bg-white/[0.06] rounded-2xl p-4 border border-white/[0.08]">
                          <h4 className="font-semibold text-white mb-3">Review Details</h4>
                          
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-white/60 text-sm">
                              <User className="h-4 w-4" />
                              <span>Reviewed by: {selectedSubmission.reviewedByName}</span>
                            </div>
                            {selectedSubmission.reviewedAt && (
                              <div className="flex items-center gap-2 text-white/60 text-sm">
                                <Clock className="h-4 w-4" />
                                <span>{formatDistanceToNow(selectedSubmission.reviewedAt, { addSuffix: true })}</span>
                              </div>
                            )}
                            
                            {/* Show different types of review feedback */}
                            {selectedSubmission.status === 'approved' && selectedSubmission.approvalNotes && (
                              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-green-400 font-medium mb-2">
                                  <CheckCircle className="h-4 w-4" />
                                  <span>Approval Notes</span>
                                </div>
                                <div className="text-white/80 text-sm leading-relaxed">{selectedSubmission.approvalNotes}</div>
                              </div>
                            )}
                            
                            {selectedSubmission.status === 'denied' && (
                              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                                {selectedSubmission.rejectionReason && (
                                  <div className="mb-3">
                                    <div className="flex items-center gap-2 text-red-400 font-medium mb-2">
                                      <XCircle className="h-4 w-4" />
                                      <span>Rejection Reason</span>
                                    </div>
                                    <div className="text-white/80 text-sm leading-relaxed">{selectedSubmission.rejectionReason}</div>
                                  </div>
                                )}
                                {selectedSubmission.feedback && (
                                  <div>
                                    <div className="flex items-center gap-2 text-red-400 font-medium mb-2">
                                      <MessageSquare className="h-4 w-4" />
                                      <span>Additional Feedback</span>
                                    </div>
                                    <div className="text-white/80 text-sm leading-relaxed">{selectedSubmission.feedback}</div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {selectedSubmission.status === 'rework_requested' && selectedSubmission.feedback && (
                              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-yellow-400 font-medium mb-2">
                                  <RotateCcw className="h-4 w-4" />
                                  <span>Rework Feedback</span>
                                </div>
                                <div className="text-white/80 text-sm leading-relaxed">{selectedSubmission.feedback}</div>
                                <div className="mt-3 text-xs text-yellow-300/80">
                                  Please address the feedback above and resubmit your content.
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Additional Media Files */}
                      {selectedSubmission.mediaFiles.length > 1 && (
                        <div className="bg-white/[0.06] rounded-2xl p-4 border border-white/[0.08]">
                          <h4 className="font-semibold text-white mb-3">Additional Media Files</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {selectedSubmission.mediaFiles.slice(1).map((mediaUrl, index) => (
                              <div key={index} className="bg-white/[0.04] rounded-lg overflow-hidden border border-white/[0.06]">
                                {selectedSubmission.mediaType === 'video' ? (
                                  <video
                                    src={mediaUrl}
                                    className="w-full h-20 object-cover"
                                    muted
                                    preload="metadata"
                                  />
                                ) : (
                                  <img
                                    src={mediaUrl}
                                    alt={`Media ${index + 2}`}
                                    className="w-full h-20 object-cover"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Thumbnail Selector Modal */}
      <AnimatePresence>
        {showThumbnailSelector && (
          <ThumbnailSelector
            videoFile={editingThumbnailId ? null : formData.mediaFiles.find(file => file.type.startsWith('video/')) || null}
            videoUrl={editingThumbnailId ? submissions.find(s => s.id === editingThumbnailId)?.mediaFiles.find(url => url.includes('.mp4') || url.includes('.mov') || url.includes('.avi') || url.includes('.webm')) : undefined}
            onThumbnailSelect={handleThumbnailSelect}
            onClose={() => {
              setShowThumbnailSelector(false);
              setEditingThumbnailId(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContentCreationPage; 